import type { SVGProps } from 'react';

export interface ReviewIconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
}

/**
 * Custom "inbox with AI sparkle" icon for the Review tab, per the Yaruta
 * Design System (Figma nodes 8315:69005 / 8315:69013 → inbox-ai). Lucide
 * doesn't ship a matching glyph; this component mirrors Lucide's
 * stroke-based conventions (24x24 viewBox, strokeWidth 2, currentColor)
 * so it drops into existing nav consumers next to Sun / CheckSquare /
 * Settings without any wrapper.
 *
 * The Figma design puts the sparkle in the extreme bottom-right corner
 * protruding past the inbox outline. The inbox body leaves that corner
 * open — its bottom edge stops at x=15 and its right edge is a short stub
 * — so the sparkle sits in the gap as a separate element rather than
 * overlapping stroke on stroke.
 */
export function ReviewIcon({
  size = 24,
  className,
  ...props
}: ReviewIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      {/* Inbox waterline — the fold where the flap meets */}
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      {/* Inbox body — the bottom edge stops at x=15 and the right edge is
          only a short stub, so the sparkle sits clear of the outline. */}
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h11" />
      <path d="M22 12v3" />
      <path d="M22 12l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11" />
      {/* AI sparkle in the bottom-right corner — Figma node 8713:38653.
          Path is in an 8×8 frame (content range 0.625–6.875); translated
          by +17,+16 to land at the exact `left:17 top:16 size:7.5` slot
          the parent icon container declares in Figma node 8315:69013.
          Stroke width is overridden so the sparkle keeps the 0.625/8
          ratio from the source rather than inheriting the 2px inbox
          stroke. */}
      <path
        transform="translate(17 16)"
        d="M3.1052 4.84364C3.0773 4.7355 3.02093 4.6368 2.94196 4.55783C2.86298 4.47885 2.76429 4.42248 2.65614 4.39458L0.738952 3.90021C0.706243 3.89092 0.677455 3.87122 0.656956 3.8441C0.636457 3.81697 0.625366 3.7839 0.625366 3.74989C0.625366 3.71589 0.636457 3.68282 0.656956 3.65569C0.677455 3.62857 0.706243 3.60887 0.738952 3.59958L2.65614 3.10489C2.76425 3.07702 2.86292 3.0207 2.94189 2.94178C3.02086 2.86286 3.07725 2.76423 3.1052 2.65614L3.59958 0.738957C3.60877 0.706119 3.62845 0.677189 3.65561 0.656581C3.68278 0.635972 3.71595 0.624817 3.75005 0.624817C3.78415 0.624817 3.81731 0.635972 3.84448 0.656581C3.87164 0.677189 3.89132 0.706119 3.90051 0.738957L4.39458 2.65614C4.42248 2.76429 4.47885 2.86299 4.55782 2.94196C4.6368 3.02094 4.73549 3.07731 4.84364 3.10521L6.76083 3.59927C6.7938 3.60836 6.82287 3.62802 6.84359 3.65523C6.86431 3.68244 6.87553 3.7157 6.87553 3.74989C6.87553 3.78409 6.86431 3.81735 6.84359 3.84456C6.82287 3.87177 6.7938 3.89143 6.76083 3.90052L4.84364 4.39458C4.73549 4.42248 4.6368 4.47885 4.55782 4.55783C4.47885 4.6368 4.42248 4.7355 4.39458 4.84364L3.9002 6.76083C3.89101 6.79367 3.87133 6.8226 3.84416 6.84321C3.817 6.86382 3.78383 6.87497 3.74973 6.87497C3.71563 6.87497 3.68247 6.86382 3.6553 6.84321C3.62813 6.8226 3.60845 6.79367 3.59926 6.76083Z"
        fill="none"
        strokeWidth={1}
      />
    </svg>
  );
}
